import '../../styles/academy-notifications.css'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  Link,
} from 'react-router-dom'

import {
  logout,
} from '../../api/auth'

import {
  getDailyDrop,
} from '../../api/drops'

import type {
  Announcement,
  HeaderResponse,
} from '../../types/header'

import type {
  MeResponse,
} from '../../types/me'

import {
  RadioPlayer,
} from '../player/RadioPlayer'
import { useAcademyNotifications } from '../../hooks/useAcademyNotifications'
import { LiveButton } from '../live/LiveButton'

interface AuthHeaderProps {
  me: MeResponse
  header: HeaderResponse
}

export function AuthHeader({
  me,
  header,
}: AuthHeaderProps) {
  const academy = useAcademyNotifications()
  const [profileOpen, setProfileOpen] =
    useState(false)

  const [logoutPending, setLogoutPending] =
    useState(false)

  const [
    notificationsOpen,
    setNotificationsOpen,
  ] = useState(false)

  const [learningOpen, setLearningOpen] =
    useState(false)

  const [dailyStreak, setDailyStreak] =
    useState<number | null>(null)

  const [
    selectedAnnouncement,
    setSelectedAnnouncement,
  ] = useState<Announcement | null>(null)

  const profileRef =
    useRef<HTMLDivElement>(null)

  const notificationsRef =
    useRef<HTMLDivElement>(null)

  const learningRef =
    useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handlePointerDown(
      event: MouseEvent,
    ) {
      const target = event.target as Node

      if (
        profileRef.current &&
        !profileRef.current.contains(target)
      ) {
        setProfileOpen(false)
      }

      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(
          target,
        )
      ) {
        setNotificationsOpen(false)
      }

      if (
        learningRef.current &&
        !learningRef.current.contains(target)
      ) {
        setLearningOpen(false)
      }
    }

    document.addEventListener(
      'mousedown',
      handlePointerDown,
    )

    return () => {
      document.removeEventListener(
        'mousedown',
        handlePointerDown,
      )
    }
  }, [])

  useEffect(() => {
    function handleEscape(
      event: KeyboardEvent,
    ) {
      if (event.key !== 'Escape') {
        return
      }

      setProfileOpen(false)
      setNotificationsOpen(false)
      setLearningOpen(false)
      setSelectedAnnouncement(null)
    }

    document.addEventListener(
      'keydown',
      handleEscape,
    )

    return () => {
      document.removeEventListener(
        'keydown',
        handleEscape,
      )
    }
  }, [])

  useEffect(() => {
    const controller =
      new AbortController()

    void getDailyDrop(
      controller.signal,
    )
      .then((response) => {
        setDailyStreak(
          response.user.streak,
        )
      })
      .catch((error) => {
        if (
          error instanceof DOMException &&
          error.name === 'AbortError'
        ) {
          return
        }

        /*
         * Daily не должен ломать Header,
         * если endpoint временно недоступен.
         */
        setDailyStreak(null)
      })

    return () => {
      controller.abort()
    }
  }, [])

  async function handleLogout() {
    if (logoutPending) {
      return
    }

    setLogoutPending(true)

    try {
      await logout()

      window.location.replace('/login/')
    } catch (error) {
      console.error(
        'Не удалось завершить сессию:',
        error,
      )

      setLogoutPending(false)
    }
  }

  const unreadCount =
    header.notifications.unread_count + academy.feed.unread_count

  const levelProgress = Math.max(
    0,
    Math.min(100, me.level.progress),
  )

  return (
    <>
      <header className="kr-header">
        <div className="kr-header__inner">
          {/* =========================
              Logo
              ========================= */}

          <a
            className="kr-header__brand kr-header__brand--logo"
            href="/"
            aria-label="Kraken Academy"
          >
            <img
              className="kr-header__logo"
              src="https://jjrr9e9l0o.cdn.twcstorage.ru/headers/logo.webp"
              alt="Kraken Academy"
            />
          </a>

<RadioPlayer />
          <LiveButton />

          {/* =========================
              Desktop navigation
              ========================= */}

          <nav
  className="kr-header__nav"
  aria-label="Основная навигация"
>
  <Link
    to="/modules/"
    className="kr-header__nav-link"
  >
    Магазин
  </Link>

  <Link
    to="/dashboard/"
    className="kr-header__nav-link"
  >
    Библиотека
  </Link>

  <Link
    to="/lab/"
    className="kr-header__nav-link"
  >
    Лаборатория
  </Link>

  <Link
    to="/knowledge/"
    className="kr-header__nav-link"
  >
    База знаний
  </Link>


  <div
    className="kr-nav-dropdown"
    ref={learningRef}
  >
    <button
      type="button"
      className={[
        'kr-header__nav-link',
        learningOpen
          ? 'kr-header__nav-link--active'
          : '',
      ].join(' ')}
      aria-expanded={learningOpen}
      onClick={() => {
        setLearningOpen(
          (value) => !value,
        )

        setProfileOpen(false)
        setNotificationsOpen(false)
      }}
    >
      Сообщество

      <ChevronIcon />
    </button>

    {learningOpen && (
      <div className="kr-nav-menu">
        <Link
          to="/leaderboard/"
          className="kr-nav-menu__item"
          onClick={() =>
            setLearningOpen(false)
          }
        >
          <span className="kr-nav-menu__icon">
            <TrophyIcon />
          </span>

          <span>
            <strong>
              Рейтинг
            </strong>

            <small>
              Таблица лидеров
            </small>
          </span>
        </Link>

<Link
  to="/blog/"
  className="kr-nav-menu__item"
  onClick={() => setLearningOpen(false)}
>
  <span className="kr-nav-menu__icon">
    <NewspaperIcon />
  </span>

  <span>
    <strong>Kraken Journal</strong>
    <small>Статьи и публикации</small>
  </span>
</Link>

        <Link
          to="/teams/"
          className="kr-nav-menu__item"
          onClick={() =>
            setLearningOpen(false)
          }
        >
          <span className="kr-nav-menu__icon">
            <ShieldIcon />
          </span>

          <span>
            <strong>
              Команды
            </strong>

            <small>
              Команды и сообщества
            </small>
          </span>
        </Link>
      </div>
    )}
  </div>
</nav>



          {/* =========================
              Actions
              ========================= */}

          <div className="kr-header__actions">
            {/* Daily */}

            <Link
              to="/daily/"
              className="kr-icon-button"
              aria-label={
                dailyStreak !== null
                  ? `Daily · серия ${dailyStreak}`
                  : 'Daily'
              }
              title={
                dailyStreak !== null
                  ? `Daily · серия ${dailyStreak}`
                  : 'Daily'
              }
            >
              <DailyIcon />

              {dailyStreak !== null && (
                <span className="kr-notification-badge">
                  {dailyStreak > 99
                    ? '99+'
                    : dailyStreak}
                </span>
              )}
            </Link>

            {/* Notifications */}

            <div
              className="kr-notifications"
              ref={notificationsRef}
            >
              <button
                type="button"
                className={[
                  'kr-icon-button',
                  notificationsOpen
                    ? 'kr-icon-button--active'
                    : '',
                ].join(' ')}
                aria-label="Уведомления"
                aria-expanded={
                  notificationsOpen
                }
                onClick={() => {
                  setNotificationsOpen(
                    (value) => !value,
                  )

                  setProfileOpen(false)
                  setLearningOpen(false)
                }}
              >
                <BellIcon />

                {unreadCount > 0 && (
                  <span className="kr-notification-badge">
                    {unreadCount > 9
                      ? '9+'
                      : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="kr-notification-panel">
                  <div className="kr-panel-heading">
                    <div>
                      <strong>
                        Уведомления
                      </strong>

                      <span>
                        {unreadCount > 0
                          ? `${unreadCount} непрочитанных`
                          : 'Всё прочитано'}
                      </span>
                    </div>

                    <button
                      type="button"
                      className="kr-panel-close"
                      aria-label="Закрыть"
                      onClick={() =>
                        setNotificationsOpen(
                          false,
                        )
                      }
                    >
                      ×
                    </button>
                  </div>

                  <div className="kr-notification-list">
                    {header.notifications.announcements.length === 0 && academy.feed.items.length === 0 ? (
                      <div className="kr-empty">
                        <BellIcon />

                        <strong>
                          Пока ничего нового
                        </strong>

                        <span>
                          Новые уведомления
                          появятся здесь.
                        </span>
                      </div>
                    ) : (
                      header.notifications
                        .announcements.map(
                          (
                            announcement,
                          ) => (
                            <button
                              key={
                                announcement.id
                              }
                              type="button"
                              className={[
                                'kr-notification-item',
                                !announcement.is_read
                                  ? 'kr-notification-item--unread'
                                  : '',
                              ].join(' ')}
                              onClick={() => {
                                setSelectedAnnouncement(
                                  announcement,
                                )

                                setNotificationsOpen(
                                  false,
                                )
                              }}
                            >
                              <span className="kr-notification-item__indicator" />

                              <span className="kr-notification-item__body">
                                <strong>
                                  {
                                    announcement.title
                                  }
                                </strong>

                                <small>
                                  {formatDate(
                                    announcement.created_at,
                                  )}
                                </small>
                              </span>

                              <span className="kr-notification-item__arrow">
                                →
                              </span>
                            </button>
                          ),
                        )
                    )}
                    {academy.feed.items.map((item) => (
                      <button key={`academy-${item.id}`} type="button"
                        className={['kr-notification-item', !item.is_read ? 'kr-notification-item--unread' : ''].join(' ')}
                        onClick={() => {
                          void academy.read(item.id)
                          setNotificationsOpen(false)
                          if (item.target_url?.startsWith('/') && !item.target_url.startsWith('//')) window.location.assign(item.target_url)
                        }}>
                        {item.image_url && (/^(https?:\/\/|\/(?!\/))/i.test(item.image_url)) ? (
                          <img
                            className="kr-achievement-notification-icon"
                            src={item.image_url}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="kr-notification-item__indicator" />
                        )}
                        <span className="kr-notification-item__body">
                          <strong>{item.title}</strong>
                          <span>{item.body}</span>
                          <small>{formatDate(item.created_at)}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="kr-academy-notification-settings">
                    {academy.feed.unread_count > 0 && <button type="button" onClick={() => { void academy.readAll() }}>Прочитать новые</button>}
                    <label><input type="checkbox" checked={academy.prefs.sound_enabled} onChange={(event) => { void academy.updatePrefs({...academy.prefs, sound_enabled:event.target.checked}) }} /> Звук</label>
                    <label>Громкость <input aria-label="Громкость уведомлений" type="range" min="0" max="100" value={academy.prefs.sound_volume} onChange={(event) => { void academy.updatePrefs({...academy.prefs, sound_volume:Number(event.target.value)}) }} /></label>
                    <label><input type="checkbox" checked={academy.prefs.global_enabled} onChange={(event) => { void academy.updatePrefs({...academy.prefs, global_enabled:event.target.checked}) }} /> Новости Академии</label>
                  </div>
                </div>
              )}
            </div>

            {/* Cart */}

            <a
              href="/cart/"
              className="kr-icon-button"
              aria-label="Корзина"
            >
              <CartIcon />

              {header.notifications
                .cart_discount.count > 0 && (
                <span className="kr-cart-badge">
                  {
                    header.notifications
                      .cart_discount.count
                  }
                </span>
              )}
            </a>

            {/* Balance */}

            <a
              href="/balance/"
              className="kr-balance"
            >
              <span className="kr-balance__icon">
                T
              </span>

              <span className="kr-balance__copy">
                <small>
                  Баланс
                </small>

                <strong>
                  {formatBalance(
                    me.balance,
                  )}
                </strong>
              </span>
            </a>

            {/* Profile */}

            <div
              className="kr-profile"
              ref={profileRef}
            >
              <button
                type="button"
                className={[
                  'kr-profile-button',
                  profileOpen
                    ? 'kr-profile-button--active'
                    : '',
                ].join(' ')}
                aria-expanded={profileOpen}
                onClick={() => {
                  setProfileOpen(
                    (value) => !value,
                  )

                  setNotificationsOpen(false)
                  setLearningOpen(false)
                }}
              >
                <span className="kr-profile-button__avatar">
                  <img
                    src={
                      me.avatar ??
                      '/images/default-avatar.png'
                    }
                    alt={me.name}
                  />

                  {me.subscription.active && (
                    <span
                      className="kr-profile-button__online"
                      title="PRO Lab активна"
                    />
                  )}
                </span>

                <span className="kr-profile-button__copy">
                  <strong>
                    {me.name}
                  </strong>

                  <small>
                    LVL {me.level.level}
                  </small>
                </span>

                <ChevronIcon />
              </button>

              {profileOpen && (
                <div className="kr-profile-menu">
                  <div className="kr-profile-card">
                    <div className="kr-profile-card__top">
                      <img
                        className="kr-profile-card__avatar"
                        src={
                          me.avatar ??
                          '/images/default-avatar.png'
                        }
                        alt={me.name}
                      />

                      <div className="kr-profile-card__identity">
                        <strong>
                          {me.name}
                        </strong>

                        <span>
                          {
                            me.leaderboard
                              .title
                          }
                        </span>
                      </div>

                      <span className="kr-profile-card__rank">
                        #
                        {
                          me.leaderboard
                            .rank
                        }
                      </span>
                    </div>

                    <div className="kr-level">
                      <div className="kr-level__heading">
                        <span>
                          Уровень{' '}
                          {me.level.level}
                        </span>

                        <strong>
                          {levelProgress}%
                        </strong>
                      </div>

                      <div className="kr-level__track">
                        <span
                          style={{
                            width:
                              `${levelProgress}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="kr-profile-stats">
                      <div>
                        <strong>
                          {
                            me.leaderboard
                              .total_completed_lessons
                          }
                        </strong>

                        <span>
                          уроков
                        </span>
                      </div>

                      <div>
                        <strong>
                          {
                            me.completed_modules
                          }
                        </strong>

                        <span>
                          модулей
                        </span>
                      </div>

                      <div>
                        <strong>
                          #
                          {
                            me.leaderboard
                              .rank
                          }
                        </strong>

                        <span>
                          в рейтинге
                        </span>
                      </div>
                    </div>

                    <div
                      className={[
                        'kr-pro-card',
                        me.subscription.active
                          ? 'kr-pro-card--active'
                          : '',
                      ].join(' ')}
                    >
                      <div>
                        <span className="kr-pro-card__label">
                          PRO
                        </span>

                        <div>
                          <strong>
                            Kraken Lab PRO
                          </strong>

                          <span>
                            {me.subscription
                              .active
                              ? getSubscriptionText(
                                  me
                                    .subscription
                                    .expires_at,
                                )
                              : 'Подписка не активна'}
                          </span>
                        </div>
                      </div>

                      <span className="kr-pro-card__status">
                        {me.subscription
                          .active
                          ? 'Активна'
                          : 'Нет'}
                      </span>
                    </div>
                  </div>

                  <div className="kr-profile-links">
                    <a
                      href={`/profile/${encodeURIComponent(
                        me.name,
                      )}`}
                    >
                      <UserIcon />

                      <span>
                        Мой профиль
                      </span>
                    </a>

                    <a href="/settings/">
                      <SettingsIcon />

                      <span>
                        Настройки
                      </span>
                    </a>

                    <a href="/faq/">
                      <HelpIcon />

                      <span>
                        Помощь и FAQ
                      </span>
                    </a>
                  </div>

                  <div className="kr-profile-menu__footer">
                    <button
                      type="button"
                      onClick={() => {
                        void handleLogout()
                      }}
                      disabled={logoutPending}
                    >
                      <LogoutIcon />

                      <span>
                        {logoutPending
                          ? 'Выходим...'
                          : 'Выйти'}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* =========================
          Mobile navigation
          ========================= */}

      <nav
        className="kr-mobile-nav"
        aria-label="Мобильная навигация"
      >
        <a href="/modules/">
          <BookIcon />
          <span>Обучение</span>
        </a>

        <a href="/lab/">
          <TerminalIcon />
          <span>Лаба</span>
        </a>

        <Link
          to="/daily/"
          className="kr-mobile-nav__primary"
        >
          <span>
            <DailyIcon />
          </span>

          <small>
            {dailyStreak !== null
              ? `Daily · ${dailyStreak}`
              : 'Daily'}
          </small>
        </Link>

        <a href="/leaderboard/">
          <TrophyIcon />
          <span>Рейтинг</span>
        </a>

        <button
          type="button"
          onClick={() => {
            setProfileOpen(true)
            setNotificationsOpen(false)
            setLearningOpen(false)

            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            })
          }}
        >
          <UserIcon />
          <span>Профиль</span>
        </button>
      </nav>

      {/* =========================
          Announcement modal
          ========================= */}

      {selectedAnnouncement && (
        <div
          className="kr-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelectedAnnouncement(
                null,
              )
            }
          }}
        >
          <article
            className="kr-announcement-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="announcement-title"
          >
            <div className="kr-announcement-modal__heading">
              <div>
                <span>
                  Объявление
                </span>

                <h2 id="announcement-title">
                  {
                    selectedAnnouncement.title
                  }
                </h2>
              </div>

              <button
                type="button"
                aria-label="Закрыть"
                onClick={() =>
                  setSelectedAnnouncement(
                    null,
                  )
                }
              >
                ×
              </button>
            </div>

            <div
              className="kr-announcement-modal__content"
              dangerouslySetInnerHTML={{
                __html:
                  selectedAnnouncement.content_html,
              }}
            />

            <div className="kr-announcement-modal__footer">
              <span>
                {formatDate(
                  selectedAnnouncement.created_at,
                )}
              </span>

              <button
                type="button"
                onClick={() =>
                  setSelectedAnnouncement(
                    null,
                  )
                }
              >
                Понятно
              </button>
            </div>
          </article>
        </div>
      )}
    </>
  )
}

function formatBalance(
  balance: string,
): string {
  const value = Number(balance)

  if (!Number.isFinite(value)) {
    return `${balance} TENT`
  }

  return `${new Intl.NumberFormat(
    'ru-RU',
    {
      maximumFractionDigits: 0,
    },
  ).format(value)} TENT`
}

function formatDate(
  value: string,
): string {
  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(
    'ru-RU',
    {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
  ).format(date)
}

function getSubscriptionText(
  expiresAt: string | null,
): string {
  if (!expiresAt) {
    return 'Подписка активна'
  }

  const date = new Date(expiresAt)

  if (Number.isNaN(date.getTime())) {
    return 'Подписка активна'
  }

  return `до ${new Intl.DateTimeFormat(
    'ru-RU',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
  ).format(date)}`
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="m7 10 5 5 5-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M3 3h2l2.3 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.6L21 6H6M10 20h.01M18 20h.01"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function DailyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M12 2.8c.8 3.1-.7 4.8-2.1 6.3-1.2 1.3-2.4 2.6-2.4 4.8a4.5 4.5 0 0 0 9 0c0-1.5-.6-2.8-1.6-4.1-.2 1.7-1 2.7-2 3.5.2-2.7-.7-5.7-.9-10.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M12 21c-1.6 0-2.8-1.1-2.8-2.6 0-1.2.7-2.1 1.7-3 .2 1.2.8 1.8 1.5 2.3.1-1 .4-1.8.9-2.6.9 1 1.5 2 1.5 3.2A2.7 2.7 0 0 1 12 21Z"
        fill="currentColor"
      />
    </svg>
  )
}

function BookIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16ZM20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="8"
        r="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="M4.5 21a7.5 7.5 0 0 1 15 0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="M19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.5 1A7 7 0 0 0 14.7 6L14.3 3h-4.6l-.4 3A7 7 0 0 0 7.6 7L5 6 3 9.4 5.1 11a7 7 0 0 0 0 2L3 14.6 5 18l2.6-1a7 7 0 0 0 1.7 1l.4 3h4.6l.4-3a7 7 0 0 0 1.7-1l2.6 1 2-3.4-2.1-1.6a7 7 0 0 0 .1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M12 3 20 6v5c0 5-3.3 8.5-8 10-4.7-1.5-8-5-8-10V6l8-3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function HelpIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <path
        d="M9.8 9a2.4 2.4 0 1 1 3.8 2c-1 .7-1.6 1.2-1.6 2.5M12 17h.01"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function NewspaperIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4 4h13v15a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4ZM17 8h3v11a2 2 0 0 1-2 2h-3M7 8h7v5H7V8ZM7 16h7M7 19h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrophyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M8 4h8v4c0 3-1.8 5-4 5s-4-2-4-5V4ZM9 20h6M12 13v7M8 6H4v2c0 2.2 1.8 4 4 4M16 6h4v2c0 2.2-1.8 4-4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TerminalIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />

      <path
        d="m7 9 3 3-3 3M12 15h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
